import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ajaxconfig } from '../ajaxconfig';
import axios from 'axios';
import { Toast, ToastContainer } from 'react-bootstrap';
import { create_key, get_key, revoke } from '../serverurl';

function Apikey() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const config = ajaxconfig();
  const [apiKeys, setApiKeys] = useState([]);
  const [revokingKey, setRevokingKey] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  // Toast management
  const showToast = (message, variant = 'success') => {
    const toast = {
      id: Date.now(),
      message,
      variant
    };
    setToasts([...toasts, toast]);
  };

  const removeToast = (id) => {
    setToasts(toasts.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    axios.get(get_key, config).then((response) => {
      setApiKeys(response.data.data);
      showToast(response.data.message);
    }).catch((error) => {
      showToast(error.response?.data?.error || 'Error fetching API keys', 'danger');
    });
  }, []);

  const handleCreateApiKey = () => {
    if (!apiKeyName.trim()) {
      showToast('API key name cannot be empty', 'warning');
      return;
    }
    
    setIsCreating(true);
    const newKey = { name: apiKeyName };

    axios.post(create_key, newKey, config).then((response) => {
      setApiKeys(response.data.data);
      setApiKeyName('');
      setIsModalOpen(false);
      showToast('API key created successfully!');
    }).catch((error) => {
      showToast(error.response?.data?.error || 'Error creating API key', 'danger');
    }).finally(() => {
      setIsCreating(false);
    });
  };

  const handleRevoke = (id) => {
    setIsRevoking(true);
    const url = revoke + id;

    axios.get(url, config).then((response) => {
      setApiKeys(apiKeys.filter(key => key.id !== id));
      showToast('API key revoked successfully!');
    }).catch((error) => {
      showToast(error.response?.data?.error || 'Error revoking API key', 'danger');
    }).finally(() => {
      setIsRevoking(false);
      setRevokingKey(null);
    });
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 4) + '...' + key.substring(key.length - 4);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* API Keys Section */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-8 p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-gray-800">API Keys Management</h2>
              <button 
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
              >
                <i className="fas fa-plus mr-2"></i> Create API Key
              </button>
            </div>
            
            {/* Payment Warning */}
            {apiKeys.length >= 2 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i className="fas fa-exclamation-circle text-yellow-400 text-xl mt-0.5"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-yellow-700">
                      <span className="font-bold">Free tier limit reached!</span> Creating additional API keys requires 
                      a paid subscription.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Documentation Link */}
            <div className="mb-6">
              <a 
                href="https://docs.cbthost.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <i className="fas fa-book mr-2"></i> View API Documentation
              </a>
            </div>
            
            {/* API Keys Table - Scrollable Container */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth token</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {apiKeys.length > 0 ? apiKeys.map((key) => (
                      <tr key={key.key}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            <i className="fas fa-key mr-2 text-blue-500"></i>
                            {key.name}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            {maskApiKey(key.key)}
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(key.key);
                                showToast('API key copied to clipboard!');
                              }}
                              className="ml-2 text-gray-400 hover:text-gray-600"
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          </div>
                        </td>

                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            {maskApiKey(key.auth_token)}
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(key.auth_token);
                                showToast('Auth token copied to clipboard!');
                              }}
                              className="ml-2 text-gray-400 hover:text-gray-600"
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <i className="fas fa-chart-bar mr-2 text-purple-500"></i>
                            {key.usage_count?.toLocaleString() || '0'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {key.revoked ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <i className="fas fa-ban mr-1"></i> Revoked
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <i className="fas fa-check-circle mr-1"></i> Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <i className="far fa-calendar mr-2 text-gray-400"></i>
                          {new Date(key.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button 
                            onClick={() => setRevokingKey(key)}
                            className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                            disabled={key.revoked}
                          >
                            <i className="fas fa-trash-alt mr-1"></i> Revoke
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center">
                            <i className="fas fa-key text-4xl text-gray-300 mb-3"></i>
                            <p>No API keys found</p>
                            <button 
                              onClick={() => setIsModalOpen(true)}
                              className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Create your first API key
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            <div className="bg-white rounded-xl shadow-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Create New API Key</h3>
                <button 
                  onClick={() => setIsModalOpen(false)} 
                  className="text-gray-500 hover:text-gray-700"
                  disabled={isCreating}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="key-name">
                  API Key Name
                </label>
                <input
                  id="key-name"
                  type="text"
                  value={apiKeyName}
                  onChange={(e) => setApiKeyName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Production Server"
                  disabled={isCreating}
                />
                <p className="mt-1 text-sm text-gray-500">Give your key a descriptive name</p>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateApiKey}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-75 flex items-center"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i> Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus mr-2"></i> Create Key
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {revokingKey && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            <div className="bg-white rounded-xl shadow-xl p-6">
              <div className="text-center mb-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold mt-3">Revoke API Key?</h3>
                <p className="text-gray-600 mt-2">
                  Are you sure you want to revoke <span className="font-medium">{revokingKey.name}</span>?
                </p>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded">
                <p className="text-red-700 flex items-start">
                  <i className="fas fa-exclamation-circle mt-0.5 mr-2 flex-shrink-0"></i>
                  <span>Warning: Applications using this key will stop working immediately.</span>
                </p>
              </div>
              
              <div className="flex justify-center space-x-4 mt-6">
                <button
                  onClick={() => setRevokingKey(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  disabled={isRevoking}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRevoke(revokingKey.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-75 flex items-center"
                  disabled={isRevoking}
                >
                  {isRevoking ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i> Revoking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt mr-2"></i> Confirm Revoke
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer position="bottom-end" className="p-4" style={{ zIndex: 9999 }}>
        {toasts.map((toast) => (
          <Toast 
            key={toast.id} 
            onClose={() => removeToast(toast.id)} 
            delay={5000} 
            autohide
            bg={toast.variant}
          >
            <Toast.Header closeButton={false}>
              <strong className="me-auto">
                {toast.variant === 'success' 
                  ? <i className="fas fa-check-circle text-success me-2"></i>
                  : toast.variant === 'danger'
                    ? <i className="fas fa-exclamation-circle text-danger me-2"></i>
                    : <i className="fas fa-info-circle text-warning me-2"></i>
                }
                Notification
              </strong>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => removeToast(toast.id)}
                aria-label="Close"
              ></button>
            </Toast.Header>
            <Toast.Body className="text-white">
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </div>
  );
};

export default Apikey;